import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import './Courses.css';

const TeacherCourses = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [studentData, setStudentData] = useState({ studentCode: '', fullNameAr: '', fullNameEn: '' });
    const [bulkData, setBulkData] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingStudentId, setEditingStudentId] = useState(null);

    useEffect(() => {
        fetchCourses();
    }, []);

    useEffect(() => {
        if (selectedCourse) {
            fetchStudents();
        }
    }, [selectedCourse]);

    const fetchCourses = async () => {
        try {
            const response = await api.get('/teacher/courses');
            if (response.data.success) {
                setCourses(response.data.data);
                if (response.data.data.length > 0) {
                    setSelectedCourse(response.data.data[0].course_id);
                }
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStudents = async () => {
        setStudentsLoading(true);
        try {
            const response = await api.get(`/teacher/courses/${selectedCourse}/students`);
            if (response.data.success) {
                setStudents(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setStudentsLoading(false);
        }
    };

    const handleAddStudent = async (e) => {
        e.preventDefault();

        const nameRegex = /^[^\d]+$/;

        if (!studentData.studentCode.trim() || !studentData.fullNameAr.trim() || !studentData.fullNameEn.trim()) {
            alert('برجاء إدخال رقم الطالب، والاسم بالعربية، والاسم بالإنجليزية');
            return;
        }

        if (!nameRegex.test(studentData.fullNameAr) || !nameRegex.test(studentData.fullNameEn)) {
            alert('لا يمكن أن يحتوي الاسم على أرقام');
            return;
        }

        setFormLoading(true);
        try {
            const url = isEditing ? `/teacher/students/${editingStudentId}` : '/teacher/students';
            const method = isEditing ? 'put' : 'post';
            
            const response = await api[method](url, {
                ...studentData,
                courseId: selectedCourse
            });

            if (response.data.success) {
                fetchStudents();
                setShowAddModal(false);
                setStudentData({ studentCode: '', fullNameAr: '', fullNameEn: '', status: 'active' });
                setIsEditing(false);
                setEditingStudentId(null);
            }
        } catch (error) {
            alert(error.response?.data?.message || t('common.error'));
        } finally {
            setFormLoading(false);
        }
    };

    const handleEditClick = (student) => {
        setStudentData({
            studentCode: student.student_code,
            fullNameAr: student.full_name_ar,
            fullNameEn: student.full_name_en || '',
            status: student.status || 'active'
        });
        setEditingStudentId(student.student_id);
        setIsEditing(true);
        setShowAddModal(true);
    };

    const handleAddClick = () => {
        setStudentData({ studentCode: '', fullNameAr: '', fullNameEn: '', status: 'active' });
        setIsEditing(false);
        setEditingStudentId(null);
        setShowAddModal(true);
    };

    const handleBulkAdd = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const lines = bulkData.split('\n').filter(line => line.trim());
            const students = lines.map(line => {
                const parts = line.split(/[\t,-]+/).map(p => p.trim());
                return {
                    studentCode: parts[0] || '',
                    fullNameAr: parts[1] || '',
                    fullNameEn: parts[2] || parts[1] || ''
                };
            }).filter(s => s.studentCode && s.fullNameAr);

            const response = await api.post('/teacher/students/bulk', {
                courseId: selectedCourse,
                students
            });
            if (response.data.success) {
                fetchStudents();
                setShowBulkModal(false);
                setBulkData('');
                alert(`${t('common.success')}: ${response.data.data.importedCount} ${t('common.students')}`);
            }
        } catch (error) {
            alert(error.response?.data?.message || t('common.error'));
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteStudent = async (studentId) => {
        if (!window.confirm(t('teacher.students.deleteConfirm'))) return;
        try {
            const response = await api.delete(`/teacher/students/${studentId}`);
            if (response.data.success) {
                fetchStudents();
            }
        } catch (error) {
            alert(error.response?.data?.message || t('common.error'));
        }
    };

    const isAr = i18n.language === 'ar';

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="teacher-courses-page fade-in">
            <div className="page-header">
                <h1>{t('teacher.courses.title')}</h1>
            </div>

            <div className="courses-grid">
                {courses.map(course => (
                    <div
                        key={course.course_id}
                        className={`course-card ${selectedCourse === course.course_id ? 'selected' : ''}`}
                        onClick={() => setSelectedCourse(course.course_id)}
                    >
                        <h3 style={{ margin: '0 0 4px' }}>{course.course_code}</h3>
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>{isAr ? course.course_name_ar : course.course_name_en}</p>
                        <p className="dept-text">{isAr ? course.department_name_ar : course.department_name_en}</p>
                        <span className="badge gender-badge">
                            {course.target_gender === 'male' ? (isAr ? 'قسم الطلاب' : 'Male') : course.target_gender === 'female' ? (isAr ? 'قسم الطالبات' : 'Female') : (isAr ? 'مشترك' : 'Both')}
                        </span>
                    </div>
                ))}
            </div>

            <div className="course-actions">
                <button className="btn btn-primary" onClick={handleAddClick}>
                    <span className="material-icons">person_add</span> {t('teacher.students.addStudent')}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowBulkModal(true)}>
                    <span className="material-icons">group_add</span> {t('teacher.students.bulkAdd')}
                </button>
                <button
                    className="btn btn-success"
                    onClick={() => navigate(`/teacher/clos/${selectedCourse}`)}
                >
                    <span className="material-icons">analytics</span> {t('teacher.courses.viewCLOs')}
                </button>
            </div>

            <div className="table-container">
                {studentsLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('teacher.students.studentCode')}</th>
                                <th>{t('teacher.students.studentName')}</th>
                                <th>{t('teacher.students.status')}</th>
                                <th>{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                                        {t('common.noData')}
                                    </td>
                                </tr>
                            ) : (
                                students.map((student, index) => (
                                    <tr key={student.student_id}>
                                        <td>{index + 1}</td>
                                        <td><strong>{student.student_code}</strong></td>
                                        <td>{isAr ? student.full_name_ar : (student.full_name_en || student.full_name_ar)}</td>
                                        <td>
                                            <span className={`badge ${
                                                student.status === 'withdrawn' ? 'badge-danger' : 
                                                student.status === 'dismissed' ? 'badge-danger' : 
                                                student.status === 'incomplete' ? 'badge-warning' : 
                                                'badge-success'
                                            }`}>
                                                {t(`teacher.students.${student.status || 'active'}`)}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    className="btn btn-warning btn-sm"
                                                    onClick={() => handleEditClick(student)}
                                                >
                                                    {t('common.edit')}
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDeleteStudent(student.student_id)}
                                                >
                                                    {t('common.delete')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? t('teacher.students.editStudent') : t('teacher.students.addStudent')}</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleAddStudent}>
                            <div className="form-group">
                                <label className="label">{t('teacher.students.studentCode')}</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={studentData.studentCode}
                                    onChange={(e) => setStudentData({ ...studentData, studentCode: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="label">{t('teacher.students.studentName')} (العربية)</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={studentData.fullNameAr}
                                    onChange={(e) => setStudentData({ ...studentData, fullNameAr: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="label">{t('teacher.students.studentName')} (English)</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={studentData.fullNameEn}
                                    onChange={(e) => setStudentData({ ...studentData, fullNameEn: e.target.value })}
                                />
                            </div>
                            
                            {isEditing && (
                                <div className="form-group">
                                    <label className="label">{t('teacher.students.status')}</label>
                                    <select 
                                        className="input"
                                        value={studentData.status}
                                        onChange={(e) => setStudentData({ ...studentData, status: e.target.value })}
                                        required
                                    >
                                        <option value="active">{t('teacher.students.active')}</option>
                                        <option value="incomplete">{t('teacher.students.incomplete')}</option>
                                        <option value="withdrawn">{t('teacher.students.withdrawn')}</option>
                                        <option value="dismissed">{t('teacher.students.dismissed')}</option>
                                    </select>
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="btn btn-outline" onClick={() => setShowAddModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                                    {formLoading ? t('common.loading') : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Add Modal */}
            {showBulkModal && (
                <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{t('teacher.students.bulkAdd')}</h2>
                            <button className="modal-close" onClick={() => setShowBulkModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleBulkAdd}>
                            <div className="form-group">
                                <label className="label">{t('teacher.students.bulkAddHint')}</label>
                                <textarea
                                    className="input"
                                    rows="10"
                                    value={bulkData}
                                    onChange={(e) => setBulkData(e.target.value)}
                                    placeholder="12345 - أحمد محمد\n12346 - محمد علي"
                                    required
                                    style={{ resize: 'vertical' }}
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-outline" onClick={() => setShowBulkModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                                    {formLoading ? t('common.loading') : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherCourses;
